<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { pair, fmtCooldown, type PairRecord } from '$stores/pair.svelte';
  import { twinsDrawer } from '$stores/twinsDrawer.svelte';
  import { b64uDecode } from '$crypto/encryption';
  import { identiconSvg } from '$game/identicon';

  /** Bumps once a minute so cooldown badges count down live. */
  let tick = $state(0);
  let timer: ReturnType<typeof setInterval> | null = null;

  onMount(() => {
    timer = setInterval(() => (tick += 1), 60_000);
  });
  onDestroy(() => {
    if (timer) clearInterval(timer);
  });

  function avatarFor(rec: PairRecord, size = 44): string {
    try {
      return identiconSvg(b64uDecode(rec.pubkeyB64), size);
    } catch {
      return identiconSvg(null, size);
    }
  }

  function pick(id: string) {
    pair.setActive(id);
  }

  function addTwin() {
    twinsDrawer.openDrawer();
  }
</script>

{#if pair.active.length === 0}
  <div class="picker-empty">
    <div class="picker-empty-title">Bond your first twin</div>
    <p class="picker-empty-sub">
      Two friends, same prompt, blind ranks. Share an invite to start a real bonded pulse.
    </p>
    <button type="button" class="btn-bond" onclick={addTwin}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M9 12l2 2 4-4" />
        <path d="M12 2a10 10 0 1 0 10 10" />
      </svg>
      Bond a twin
    </button>
  </div>
{:else}
  {#key tick}
    <div class="picker-row" role="radiogroup" aria-label="Choose a twin">
      {#each pair.active as rec (rec.pairId)}
        {@const cooldown = pair.cooldownRemainingMs(rec.pairId)}
        {@const isActive = pair.activeId === rec.pairId}
        <button
          type="button"
          class="twin-tile"
          class:active={isActive}
          class:cooldown={cooldown > 0}
          role="radio"
          aria-checked={isActive}
          onclick={() => pick(rec.pairId)}
        >
          <span class="twin-avatar">{@html avatarFor(rec)}</span>
          <span class="twin-handle">@{rec.handle}</span>
          {#if cooldown > 0}
            <span class="twin-status cooldown">{fmtCooldown(cooldown)}</span>
          {:else}
            <span class="twin-status ready">Ready</span>
          {/if}
        </button>
      {/each}
      <button
        type="button"
        class="twin-tile add-tile"
        aria-label="Bond another twin"
        onclick={addTwin}
      >
        <span class="twin-avatar add">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        <span class="twin-handle add">Add twin</span>
      </button>
    </div>
  {/key}
{/if}

<style>
  .picker-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 10px;
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    text-align: center;
  }
  :global(body.brutal) .picker-empty {
    border-style: solid;
    border-width: 2px;
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .picker-empty-title {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .picker-empty-sub {
    font-size: 12px;
    color: var(--text-2);
    line-height: 1.45;
    max-width: 280px;
  }
  .btn-bond {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    background: linear-gradient(135deg, var(--gold) 0%, var(--hot) 100%);
    color: var(--text-on-light);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: var(--r-md);
    font-size: 13px;
    font-weight: 900;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    box-shadow:
      var(--depth-md) var(--hot-deep),
      var(--glow-hot);
    transition: transform 100ms var(--spring-press), box-shadow 100ms var(--spring-press);
  }
  .btn-bond::before {
    content: '';
    position: absolute;
    top: 18%;
    left: 8%;
    right: 8%;
    height: 28%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), transparent);
    border-radius: var(--r-pill);
    pointer-events: none;
  }
  .btn-bond:active {
    transform: translateY(4px);
    box-shadow: 0 0 0 0 var(--hot-deep), var(--glow-hot);
  }
  :global(body.brutal) .btn-bond {
    background: var(--text);
    color: var(--bg);
    border: 2px solid var(--text);
    box-shadow: 4px 4px 0 0 var(--text);
  }
  :global(body.brutal) .btn-bond::before {
    display: none;
  }
  .btn-bond svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }

  .picker-row {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 4px 2px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 2px;
  }
  .picker-row::-webkit-scrollbar {
    display: none;
  }
  .twin-tile {
    flex: 0 0 clamp(84px, 24vw, 96px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 10px 8px;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius);
    transition: var(--transition);
    cursor: pointer;
    scroll-snap-align: start;
  }
  .twin-tile:hover {
    background: var(--surface-2);
  }
  .twin-tile.active {
    border-color: var(--primary);
    background: rgba(0, 212, 255, 0.06);
    box-shadow: 0 0 0 1px rgba(0, 212, 255, 0.35);
  }
  :global(body.brutal) .twin-tile {
    border-width: 2px;
  }
  :global(body.brutal) .twin-tile.active {
    background: var(--bg);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .twin-tile.cooldown {
    opacity: 0.65;
  }
  .twin-tile.cooldown.active {
    opacity: 0.85;
  }
  .twin-avatar {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    display: grid;
    place-items: center;
  }
  :global(body.brutal) .twin-avatar {
    border-width: 2px;
  }
  .twin-avatar :global(svg) {
    display: block;
    width: 100%;
    height: 100%;
  }
  .twin-avatar.add {
    background: transparent;
    border-style: dashed;
  }
  .twin-avatar.add svg {
    width: 18px;
    height: 18px;
    stroke: var(--text-3);
  }
  .twin-handle {
    font-size: 11px;
    font-weight: 700;
    color: var(--text);
    max-width: 76px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .twin-handle.add {
    color: var(--text-3);
  }
  .twin-status {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .twin-status.ready {
    color: var(--accent-green);
  }
  .twin-status.cooldown {
    color: var(--text-3);
  }
  :global(body.brutal) .twin-status.ready {
    color: var(--text);
  }
  .add-tile {
    border-style: dashed;
  }
</style>
